<template lang="pug"  src="./leaders.pug"> </template>
<style lang="scss" src="./leaders.scss" scoped> </style>

<script>
import router from '@/router'
import validate from '@/mixins/validate'
import scroll from '@/utils/scroll'
import request from '@/utils/request'

import { mapState, mapGetters } from 'vuex'
import { validationMixin } from 'vuelidate'
import { numeric } from 'vuelidate/lib/validators'
import lockDialog from '@/components/LockDialog'

export default {
    components: {
        'v-lock-dialog': lockDialog
    },
    mixins: [validationMixin, validate],
    data: () => ({
        filters: {
            leaderId: null,
            fromDate: null,
            toDate: null,
            leaderEmail: null,
            query: null
        },
        refillDialog: false,
        refillSum: null,
        idToRefill: null,
        leaderBalance: null,
        refillPromoBalance: null,
        lockDialog: false,
        activeAccountId: null,
        feeTypes: ['fee', 'telephonyFee'],
        newFee: null,
        sorting: {},
        loading: false,
        changeLimits: {
            data: {
                clientBase: null,
                accountLimit: null
            },
            dialog: false,
            resetFeeDate: null
        },
        newLimitedTo: null,
        newAmount: null,
        newBrandCode: null
    }),
    validations: {
        filters: {
            leaderId: {
                numeric
            }
        },
        newFee: {
            numeric: function(value) {
                if (!value) {
                    return true
                }

                return /^\d+$/.test(value) && value >= 0 && value <= 100
            }
        },
        changeLimits: {
            data: {
                clientBase: {
                    max: (value) => value < 1000000
                },
                accountLimit: {
                    max: (value) => value < 10000
                }
            }
        }
    },
    computed: {
        ...mapGetters(['permissions']),
        ...mapState({
            leaders: (state) => state.leader.items,
            leadersEmails: (state) => state.data.leaders,
            appliedFilters: (state) => state.leader.filters,
            isTableLoad: (state) => state.leader.isLoaded,
            pagination: state => state.leader.pagination
        }),
        refillDisabled: function() {
            const lessThenBalance = parseInt(this.leaderBalance) + parseInt(this.refillSum) < 0
            return !this.refillSum || !this.refillSum.match(/^-?\d+$/) || this.refillPromoBalance ? false : lessThenBalance
        },
        canCreateAccount: function() {
            return this.permissions && this.permissions.includes('create-account')
        },
        headers: function() {
            return [
                { text: 'id', value: 'id' },
                { text: 'email', value: 'email' },
                { text: 'firstName', value: '"firstName"' },
                { text: 'lastName', value: '"lastName"' },
                { text: 'phoneNumber', value: '"phoneNumber"' },
                { text: 'balance', value: 'balance' },
                { text: 'fee', value: 'fee' },
                { text: 'telephonyFee', value: 'telephonyFee', sortable: false },
                { text: 'brandCode', value: 'brandCode', sortable: false },
                { text: 'clients', sortable: false },
                { text: 'clientBase', value: '"clientBase"' },
                { text: 'distributedClients', sortable: false },
                { text: 'businesses', sortable: false },
                { text: 'accountLimit', value: '"accountLimit"' },
                { text: 'limitedTo', value: '"limitedTo"' },
                { text: 'processedAfterRestrictions', value: '"processedAfterRestrictions"' },
                { text: 'purchased', sortable: false },
                { text: 'promoBalance', value: '"promoBalance"' },
                { text: 'autoPromoBalance', value: '"autoPromoBalance"' },
                { text: 'amountToPay', value: '"amountToPay"' },
                { text: 'amountPaid', value: '"amountPaid"' },
                { text: 'createdAt', value: '"createdAt"' },
                { text: 'actions', sortable: false }
            ].map(it => Object.assign(it, {
                text: this.$vuetify.lang.t(`$vuetify.pages.Leaders.table.${it.text}`)
            }))
        },
        canLockUserAccount: function() {
            return this.permissions && this.permissions.includes('lock-account')
        }
    },
    watch: {
        sorting: {
            handler() {
                this.getLeaders()
            },
            deep: true
        }
    },
    created: function() {
        this.$store.dispatch('getLeaders')
        this.filters = Object.assign({}, this.appliedFilters)
        this.getLeaders()
    },
    beforeRouteLeave: function(to, from, next) {
        this.$store.commit('leader/SET_PAGINATION', { page: 1, length: null })
        next()
    },
    methods: {
        addAccount() {
            this.$store.commit('createAccount/SET_ACTIVE_TYPE', 'leader')
            router.push('create-account')
        },
        unlockAccount(id) {
            if (this.loading) return
            this.loading = true

            this.$store.dispatch('changeActivity', {
                id, accountType: 'leader'
            }).finally(() => {
                this.loading = false
            })
        },
        onPurchasedChange(id) {
            this.$store.dispatch('changePurchased', id).then(status => {
                const account = this.leaders.findIndex(it => it.leaderId == id)
                const value = status === 204 ? !account.purchased : false

                if (account) {
                    this.$set(account, 'purchased', value)
                }
            })
        },
        changeFromDate(date) {
            this.filters.fromDate = date
        },
        changeToDate(date) {
            this.filters.toDate = date
        },
        submit() {
            this.$store.commit('leader/SET_FILTERS', Object.assign({}, this.filters))
            this.getLeaders()
        },
        getLeaders(page = 1, isScrollEnable = false, excel = false) {
            if (this.$v.$invalid) {
                this.$v.$touch()
            } else {
                this.$store.dispatch('leader/getLeaders', {
                    excel,
                    page,
                    column: this.sorting.sortBy,
                    direction: this.sorting.descending ? 'desc' : 'asc',
                    ...this.appliedFilters
                }).then(_ => {
                    if (isScrollEnable) {
                        scroll(this.$refs.table)
                    }
                })
            }
        },
        resetLeaders() {
            this.filters = Object.assign({}, {
                leaderId: null,
                fromDate: null,
                toDate: null,
                leaderEmail: null,
                query: null
            })
            this.$store.commit('leader/RESET_LEADERS')
            this.getLeaders()
        },
        onPageChange(page) {
            if (page !== this.pagination.page) {
                this.getLeaders(page, true)
            }
        },
        openDialog(dialogType, accountId, balance, refillPromoBalance = false) {
            this[dialogType] = true
            this.activeAccountId = accountId
            this.leaderBalance = balance
            this.refillPromoBalance = refillPromoBalance
        },
        async refillBalance() {
            const res = await this.$store.dispatch('refillBalance', {
                accountId: this.activeAccountId,
                sum: this.refillSum * 100,
                promo: this.refillPromoBalance
            })

            if (res.status == 200) {
                const index = this.leaders.findIndex(it => it.leaderId == this.activeAccountId)

                if (index != -1) {
                    const leader = this.leaders[index]
                    const balanceType = this.refillPromoBalance ? 'promoBalance' : 'balance'
                    leader[balanceType] = parseInt(leader[balanceType]) + parseInt(this.refillSum * 100)
                }
            }

            this.resetData()
            this.refillDialog = false
        },
        resetData() {
            this.activeAccountId = null

            if (this.refillDialog) {
                this.refillSum = null
                this.refillPromoBalance = null
            }
        },
        closeDialog(dialogType) {
            this.resetData()
            this[dialogType] = false
        },
        onEditDialogOpen(item, type = 'fee') {
            this.newFee = item[type] || 0
        },
        saveFee(accountId, index, type = 'fee') {
            if (this.$v.newFee.$invalid) {
                this.$v.newFee.$touch()
                return
            }

            this.$store.dispatch('editUserFee', {
                accountId, type,
                newFee: this.newFee
            }).then(status => {
                if (status && status === 204) {
                    this.$set(this.leaders[index], type, this.newFee || 0)

                    this.$nextTick(() => {
                        this.newFee = null
                    })
                }
            })
        },
        async toggleTelephony(accountId, index) {
            await request({ method: 'patch', url: `/account/telephony/${accountId}` })
                .then(res => {
                    if (res.status === 204) {
                        const leader = this.leaders[index]
                        this.$set(leader, 'includeTelephony', !leader.includeTelephony)

                        this.$store.dispatch('addMessage', {
                            type: 'success',
                            message: 'success'
                        })
                    }
                }).catch(err => {
                    const message = err.response && err.response.data && err.response.data.message

                    if (message) {
                        this.$store.dispatch('addMessage', {
                            type: 'error',
                            message
                        }, { root: true })
                    }
                })
        },
        exportToExcel() {
            this.getLeaders(1, false, true)
        },
        changeLimitsHandler({ clientBase, accountLimit, unlimitedRights, leaderId, resetFeeDate }) {
            this.changeLimits = {
                data: {
                    clientBase,
                    accountLimit,
                    unlimitedRights,
                    accountId: leaderId
                },
                dialog: true,
                resetFeeDate: !!resetFeeDate
            }
        },
        unlimitedRightsHandler(value) {
            if (value) {
                this.changeLimits.data.clientBase = null
                this.changeLimits.data.accountLimit = null
            }
        },
        resetChangeLimits() {
            this.changeLimits = {
                data: {
                    clientBase: null,
                    accountLimit: null
                },
                dialog: false,
                resetFeeDate: null
            }
        },
        async saveLimits() {
            if (this.$v.changeLimits.$invalid) {
                this.$v.changeLimits.$touch()
                return
            }

            if (this.loading) return

            this.loading = true

            if (!this.changeLimits.resetFeeDate) {
                request({
                    method: 'post',
                    url: `/account/${this.changeLimits.data.accountId}/resets/fee/exclude`
                })
                    .then(() => null)
                    .catch(err => {
                        console.error(err)
                    })
            }

            await request({
                method: 'post',
                url: 'limits',
                data: this.changeLimits.data
            }).then(res => {
                this.getLeaders()
                this.changeLimits.dialog = false
            }).catch(err => {
                if (err.response && err.response.data && err.response.data.message) {
                    this.$store.dispatch('addMessage', {
                        type: 'error',
                        message: this.$vuetify.t(`$vuetify.messages.error.limits.${err.response.data.message}`),
                        locale: false
                    }, { root: true })
                }
            }).finally(() => {
                this.loading = false
            })
        },
        showEditLimitedTo({ limitedTo, processedAfterRestrictions }) {
            return limitedTo && !processedAfterRestrictions
        },
        async saveLimitedTo(accountId, index) {
            if (this.loading || !this.newLimitedTo) return

            this.loading = true

            await request({
                method: 'put',
                url: '/limit/date',
                data: { accountId, limitedTo: this.newLimitedTo }
            }).then(res => {
                if (res.data) {
                    this.leaders[index].limitedTo = this.newLimitedTo
                }
            }).catch(err => {
                if (err.response && err.response.data && err.response.data.message) {
                    this.$store.dispatch('addMessage', {
                        type: 'error',
                        message: this.$vuetify.t(`$vuetify.messages.error.limits.${err.response.data.message}`),
                        locale: false
                    }, { root: true })
                }
            }).finally(() => {
                this.loading = false
                this.newLimitedTo = null
            })
        },
        async updateAmountField(accountId, index, field) {
            if (this.loading || !this.newAmount) return

            this.loading = true

            const res = await this.$store.dispatch('updateAccountAmount', {
                accountId, field,
                amount: parseInt(this.newAmount)
            })

            if (res == 204) {
                this.leaders[index][field] = parseInt(this.newAmount)
            }

            this.loading = false
            this.newAmount = null
        },
        disabledUpdateAmountPaid(amountToPay, amountPaid) {
            return amountPaid >= amountToPay
        },
        openAmountDialog(value) {
            this.newAmount = value
        },
        async saveBrandCode(accountId, index) {
            if (this.loading) return

            this.loading = true

            await request({
                method: 'put',
                url: `/account/${accountId}/brand-code`,
                data: { brandCode: this.newBrandCode }
            }).then(res => {
                console.log(res.status)
                if (res.status == 204) {
                    this.$set(this.leaders, index, Object.assign({}, this.leaders[index], {
                        brandCode: this.newBrandCode
                    }))
                }
            }).catch(err => {
                if (err.response && err.response.data && err.response.data.message) {
                    this.$store.dispatch('addMessage', {
                        type: 'error',
                        message: this.$vuetify.t(`$vuetify.messages.error.${err.response.data.message}`),
                        locale: false
                    }, { root: true })
                }
            }).finally(() => {
                this.loading = false
                this.newBrandCode = null
            })
        }
    }
}
</script>
